import { PaybackPeriodOptions } from '../stores/FundingCalculator/types/fundingCalculator';
import UnderwritingIcon from '@assets/icons/underwriting.svg';
import BenchmarksIcon from '@assets/icons/tour.svg';
import ROASIcon from '@assets/icons/analytics.svg';
import FundingEligibilityIcon from '@assets/icons/invoice.svg';
import { Module } from '@stores/Modules/types/modules';
import GettingStartedIcon from '@assets/icons/getting-started-icon.svg';

const USER_ROLES = {
  VIEWER: 'Viewer',
  ADMIN: 'Admin',
};

const USER_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'In-Active',
  INVITE_EXPIRED: 'Invite expired',
  INVITED: 'Invited',
  PENDING: 'Failed',
  PENDING_ROLE_UPDATE: 'Pending',
};

const ICON_COLORS = ['#5300A7', '#00866E', '#728600', '#006686', '#00866E', '#861800', '#002686'];

const STATUS_COLORS = {
  succes: {
    color: '#162936',
    background: 'transparent',
  },
  error: {
    color: '#162936',
    background: '#FFD2CC',
  },
  warning: {
    color: '#162936',
    background: '#FFE0B2',
  },
  default: {
    color: '#162936',
    background: '#9AD6D1',
  },
};

const CURRENCY_LIMIT_10K = 10000;
const CURRENCY_LIMIT_100K = 100000;
const CURRENCY_LIMIT_10M = 10000000;
const CURRENCY_LIMIT_100M = 100000000;

const DEFAULT_MARKETING_SPENDS = 250000;
const MULTIPLICATION_FACTOR_DEFAULT_MONTHLY_REVENUE = 2;
const MULTIPLICATION_FACTOR_DEFAULT_OPERATING_EXPENSES = 0.5;
const MULTIPLICATION_FACTOR_DEFAULT_PREVIOUS_MONTH_BALANCE = 1.5;

const DEFAULT_PAYBACK_PERIOD = 12;
const DEFAULT_PVX_INTEREST = 2;
const DEFAULT_PVX_FINANCING = 0.8;
const DEFAULT_REVENUE_CHURN = 0;
const DEFAULT_UA_INCREASE = 0.025;

const ROAS_PAYBACK_PERIOD_VALUE = [
  { value: 1, label: '1' },
  { value: 3, label: '3' },
  { value: 6, label: '6' },
  { value: 9, label: '9' },
  { value: 12, label: '12' },
  { value: 15, label: '15' },
  { value: 18, label: '18' },
  { value: 24, label: '24' },
  { value: 36, label: '36' },
];

const PAYBACK_PERIOD_OPTIONS = [
  { value: 12, label: '12' },
  { value: 18, label: '18' },
  { value: 24, label: '24' },
  { value: 36, label: '36' },
];

const PVX_FINANCING_OPTIONS = [
  { value: 20, label: '20' },
  { value: 30, label: '30' },
  { value: 40, label: '40' },
  { value: 50, label: '50' },
  { value: 60, label: '60' },
  { value: 70, label: '70' },
  { value: 80, label: '80' },
];

const REVENUE_CHURN_OPTIONS = [
  { value: 0, label: '0' },
  { value: 5, label: '-5' },
  { value: 10, label: '-10' },
  { value: 15, label: '-15' },
  { value: 20, label: '-20' },
  { value: 25, label: '-25' },
  { value: 30, label: '-30' },
];

const UA_INCREASE_OPTIONS = [
  { value: 0, label: '0' },
  { value: 2.5, label: '' },
  { value: 5, label: '5' },
  { value: 7.5, label: '' },
  { value: 10, label: '10' },
  { value: 12.5, label: '' },
  { value: 15, label: '15' },
];

const DEFAULT_PAYBACK_PERIOD_OPTIONS: PaybackPeriodOptions = {
  12: [
    { month: 1, value: 20 },
    { month: 3, value: 75 },
    { month: 6, value: 100 },
    { month: 9, value: 120 },
    { month: 12, value: 135 },
  ],
  18: [
    { month: 1, value: 20 },
    { month: 3, value: 40 },
    { month: 6, value: 60 },
    { month: 9, value: 80 },
    { month: 12, value: 100 },
    { month: 15, value: 120 },
    { month: 18, value: 140 },
  ],
  24: [
    { month: 1, value: 10 },
    { month: 3, value: 30 },
    { month: 6, value: 50 },
    { month: 9, value: 70 },
    { month: 12, value: 90 },
    { month: 15, value: 105 },
    { month: 18, value: 120 },
    { month: 24, value: 140 },
  ],
  36: [
    { month: 1, value: 5 },
    { month: 3, value: 15 },
    { month: 6, value: 25 },
    { month: 9, value: 35 },
    { month: 12, value: 45 },
    { month: 15, value: 65 },
    { month: 18, value: 85 },
    { month: 24, value: 110 },
    { month: 36, value: 140 },
  ],
};

const DEFAULT_LATEST_MONTH_FINANCIALS = {
  netRevenue: 0,
  marketingSpends: 0,
  latestMonthCashBalance: 0,
  operatingExpenses: 0,
  profitBeforeTaxes: 0,
};

const CHART_TAGS = {
  CUMULATIVE_ROAS: 'Cumulative ROAS',
  MONTHLY_MARKETING_SPENDS_SURPLUS_EXPENDITURE: 'Monthly Marketing Spends - surplus expenditure',
  MONTHLY_MARKETING_SPENDS_BALANCE_DELTA: 'Monthly Marketing Spends - balance delta',
  PROFIT_BEFORE_TAXES: 'Profit Before Taxes',
  PROFIT_BEFORE_TAXES_CUMULATIVE: 'Profit Before Taxes Cumulative',
  INTEREST_PAID_BAR: 'Interest Paid - BAR',
  INTEREST_PAID_PIE: 'Interest Paid - PIE',
  REVENUE: 'Revenue',
};

const fundingPerks = [
  'Receive up to 80% of your marketing budget in cash every month',
  'Funding is collateralised by only the cohorts within the PvX funding period',
  'Pay a monthly revenue share proportionate to the amount of funding received, capped at 100% + Cash Cost',
  'Request up to a $100M facility to draw down upon as you scale UA spends',
];

const APP_STORE_LINK_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^https:\/\/apps\.apple\.com\/[a-z]{2}\/app\/[a-zA-Z0-9\-]+\/id\d{9,}$/;
const APP_STORE_LINK_REGEX_2 = /^https:\/\/apps\.apple\.com\/app\/id\/\d{9,}$/;
const PLAY_STORE_LINK_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^https:\/\/play\.google\.com\/store\/apps\/details\?id=[a-zA-Z0-9\._]+(&[a-zA-Z0-9=_\-]+)*$/;
const STEAM_STORE_LINK_REGEX = /^https:\/\/store\.steampowered\.com\/app\/\d+\/[a-zA-Z0-9_]+\/?$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PLAY_STORE_APP_ID_REGEX = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+[0-9a-z_]$/i;
const APP_STORE_APP_ID_REGEX = /^id[0-9]+/;

const APPLICATION_STORES = {
  APP_STORE: 'APP_STORE',
  PLAY_STORE: 'PLAY_STORE',
} as const;

const ATTRIBUTED_CHANNEL_OPTIONS = [
  {
    value: 'PvX Team',
    label: 'PvX Team',
  },
  {
    value: 'Conference',
    label: 'Conference',
  },
  {
    value: 'Investor',
    label: 'Investor',
  },
  {
    value: 'Founder',
    label: 'Founder',
  },
  {
    value: 'Advertisement',
    label: 'Advertisement',
  },
  {
    value: 'Other',
    label: 'Other',
  },
];

const MONTHLY_MARKETTING_SPENDS_OPTIONS = [
  {
    value: 'Under $100,000',
    label: 'Under $100,000',
  },
  {
    value: '$100,000 - $250,000',
    label: '$100,000 - $250,000',
  },
  {
    value: '$250,000 - $500,000',
    label: '$250,000 - $500,000',
  },
  {
    value: '$500,000 - $1,000,000',
    label: '$500,000 - $1,000,000',
  },
  {
    value: '> $1,000,000',
    label: '> $1,000,000',
  },
];

const GETTING_STARTED_MODULES: Module[] = [
  {
    id: 'funding-eligibility-001',
    name: 'Funding Eligibility',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'funding-eligibility-sub-001',
        name: 'Funding Eligibility',
        url: 'funding-eligibility',
        embedId: '',
        submoduleType: 'FUNDING_ELIGIBILITY',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
      },
    ],
    metadata: `{ "icon": "${FundingEligibilityIcon}" }`,
  },
  {
    id: 'underwriting-report-001',
    name: 'Underwriting Report',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'underwriting-report-sub-001',
        name: 'Underwriting Report',
        url: 'underwriting',
        embedId: '',
        submoduleType: 'UNDERWRITTING',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
      },
    ],
    metadata: `{ "icon": "${UnderwritingIcon}" }`,
  },
  {
    id: 'benchmarks-001',
    name: 'Benchmarks',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'benchmarks-sub-001',
        name: 'Benchmarks',
        url: 'benchmarks',
        embedId: '',
        submoduleType: 'BENCHMARKS',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
      },
    ],
    metadata: `{ "icon": "${BenchmarksIcon}" }`,
  },
  {
    id: 'financial-projections-001',
    name: 'Financial Projections',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'financial-projections-sub-001',
        name: 'Financial Projections',
        url: 'financial-projections',
        embedId: '',
        submoduleType: 'FINANCIAL_PROJECTIONS',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
      },
    ],
    metadata: `{ "icon": "${ROASIcon}" }`,
  },
];

const GETTING_STARTED_DEMO_MODULES: Module[] = [
  {
    id: 'getting-started-demo-module',
    name: 'Getting Started',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'getting-started-demo-sub-module',
        name: 'Getting Started',
        url: 'demo',
        embedId: '',
        submoduleType: 'GETTING_STARTED_DEMO',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: false,
      },
    ],
    metadata: `{ "icon": "${GettingStartedIcon}" }`,
  },
  {
    id: 'funding-eligibility-001',
    name: 'Funding Eligibility',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'funding-eligibility-sub-001',
        name: 'Funding Eligibility',
        url: 'funding-eligibility',
        embedId: '',
        submoduleType: 'FUNDING_ELIGIBILITY',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
      },
    ],
    metadata: `{ "icon": "${FundingEligibilityIcon}" }`,
  },
  {
    id: 'underwriting-report-001',
    name: 'Underwriting Report',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'underwriting-report-sub-001',
        name: 'Underwriting Report',
        url: 'underwriting',
        embedId: '',
        submoduleType: 'UNDERWRITTING',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
      },
    ],
    metadata: `{ "icon": "${UnderwritingIcon}" }`,
  },
  {
    id: 'benchmarks-001',
    name: 'Benchmarks',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'benchmarks-sub-001',
        name: 'Benchmarks',
        url: 'benchmarks',
        embedId: '',
        submoduleType: 'BENCHMARKS',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
      },
    ],
    metadata: `{ "icon": "${BenchmarksIcon}" }`,
  },
  {
    id: 'financial-projections-001',
    name: 'Financial Projections',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'financial-projections-sub-001',
        name: 'Financial Projections',
        url: 'financial-projections',
        embedId: '',
        submoduleType: 'FINANCIAL_PROJECTIONS_DEMO',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
      },
    ],
    metadata: `{ "icon": "${ROASIcon}" }`,
  },
];

const DEMO_USER_EMAILS = ['demo@pvxpartners.com'];

const SCHEDULE_CALL_LINK = 'https://pvxpartners.pipedrive.com/scheduler/92o4OGcv/meeting';

const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  DEFAULT: 'default',
} as const;

const enum SUBMODULE_VIEW {
  TAB = 'TAB',
}

const REQUESTED_SUBMODULES_META: Record<
  string,
  {
    title: string;
    subTitle: string;
    body: string;
    images: string[];
  }
> = {
  FINANCIAL_PROJECTIONS: {
    title: 'Access ROAS Projections,',
    subTitle: 'for smarter financial planning',
    body: 'Use our projections model trained on your data and fine-tuned with category insights to forecast cohort-level ROAS, helping you plan your cash flow',
    images: [
      'demo-dashboards/financial_projections_1.png',
      'demo-dashboards/financial_projections_2.png',
      'demo-dashboards/financial_projections_3.png',
    ],
  },
};

enum SelfServeDataAccessStatus {
  PENDING_USER_CREATION = 'PENDING_USER_CREATION',
  PENDING_NDA_CREATION = 'PENDING_NDA_CREATION',
  READY_TO_SEND_NDA = 'READY_TO_SEND_NDA',
  PENDING_NDA_SIGNATURE = 'PENDING_NDA_SIGNATURE',
  ACCESS_GRANTED = 'ACCESS_GRANTED',
  PENDING_ACCESS = 'PENDING_ACCESS',
  DATA_FETCH_FAILED = 'DATA_FETCH_FAILED',
  DATA_FETCH_PARTIAL = 'DATA_FETCH_PARTIAL',
  DASHBOARD_CREATION_FAILED = 'DASHBOARD_CREATION_FAILED',
  DATA_FETCH_SUCCESSFUL = 'DATA_FETCH_SUCCESSFUL',
  PUBLISHED = 'PUBLISHED',
  CALL_SCHEDULED = 'CALL_SCHEDULED',
  REOPEN = 'REOPENED',
  DELETED = 'DELETED',
}

export const SelfServeDataAccessStatusMap: Record<SelfServeDataAccessStatus, string> = {
  [SelfServeDataAccessStatus.PENDING_USER_CREATION]: 'Pending User Creation',
  [SelfServeDataAccessStatus.PENDING_NDA_CREATION]: 'Pending NDA Creation',
  [SelfServeDataAccessStatus.READY_TO_SEND_NDA]: 'Ready To Send NDA',
  [SelfServeDataAccessStatus.PENDING_NDA_SIGNATURE]: 'Pending NDA Signature',
  [SelfServeDataAccessStatus.PENDING_ACCESS]: 'Pending Access',
  [SelfServeDataAccessStatus.CALL_SCHEDULED]: 'Call Scheduled',
  [SelfServeDataAccessStatus.ACCESS_GRANTED]: 'Access Granted',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED]: 'Data Fetch Failed',
  [SelfServeDataAccessStatus.DATA_FETCH_PARTIAL]: 'Data Fetch Partial',
  [SelfServeDataAccessStatus.DASHBOARD_CREATION_FAILED]: 'Dashboard Creation Failed',
  [SelfServeDataAccessStatus.DATA_FETCH_SUCCESSFUL]: 'Data Fetch Successful',
  [SelfServeDataAccessStatus.PUBLISHED]: 'Published',
  [SelfServeDataAccessStatus.REOPEN]: 'Reopened',
  [SelfServeDataAccessStatus.DELETED]: 'Deleted',
};

enum MODES {
  EDIT = 'EDIT',
  CREATE = 'CREATE',
  LISTING = 'LISTING',
}

export const SELF_SERVE_PENDING_USER_CREATION_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.PENDING_USER_CREATION,
]);
export const SELF_SERVE_PENDING_NDA_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.PENDING_NDA_CREATION,
  SelfServeDataAccessStatus.READY_TO_SEND_NDA,
  SelfServeDataAccessStatus.PENDING_NDA_SIGNATURE,
]);
export const SELF_SERVE_PENDING_ACCESS_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.PENDING_ACCESS,
  SelfServeDataAccessStatus.CALL_SCHEDULED,
]);
export const SELF_SERVE_PROCESSING_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.ACCESS_GRANTED,
  SelfServeDataAccessStatus.DATA_FETCH_PARTIAL,
  SelfServeDataAccessStatus.DATA_FETCH_SUCCESSFUL,
]);
export const SELF_SERVE_PROCESSED_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.PUBLISHED,
]);
export const SELF_SERVE_FAILED_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.DATA_FETCH_FAILED,
  SelfServeDataAccessStatus.DASHBOARD_CREATION_FAILED,
]);

export enum DataAccessProvider {
  APPS_FLYER = 'APPS_FLYER',
  ADJUST = 'ADJUST',
  BIGQUERY = 'BIGQUERY',
  GCS = 'GCS',
  S3 = 'S3',
  MYSQL = 'MYSQL',
  SNOWFLAKE = 'SNOWFLAKE',
  GOOGLE_DRIVE = 'GOOGLE_DRIVE',
  AGGREGATED_DATA = 'AGGREGATED_DATA',
  NONE = 'NONE',
}

export const MMP_SOURCE_MAP: Record<DataAccessProvider, string> = {
  [DataAccessProvider.APPS_FLYER]: 'AppsFlyer',
  [DataAccessProvider.ADJUST]: 'Adjust',
  [DataAccessProvider.NONE]: 'None',
  [DataAccessProvider.BIGQUERY]: 'BigQuery',
  [DataAccessProvider.GCS]: 'Google Cloud Storage',
  [DataAccessProvider.S3]: 'Amazon S3',
  [DataAccessProvider.MYSQL]: 'MySQL',
  [DataAccessProvider.SNOWFLAKE]: 'Snowflake',
  [DataAccessProvider.GOOGLE_DRIVE]: 'Cloud Storage',
  [DataAccessProvider.AGGREGATED_DATA]: 'Aggregated Cohort Data',
};

enum GameStatus {
  FUNDED = 'FUNDED',
  NOT_FUNDED = 'NOT_FUNDED',
  SELF_SERVED = 'SELF_SERVED',
}

enum PricingMethodology {
  SIMPLE_INTEREST = 'SIMPLE_INTEREST',
  DYNAMIC_RETURN_CAP = 'DYNAMIC_RETURN_CAP',
  DYNAMIC_RETURN_CAP_V2 = 'DYNAMIC_RETURN_CAP_V2',
  CUSTOM = 'CUSTOM',
  CUSTOM_PLAYSOME = 'CUSTOM_PLAYSOME',
  CUSTOM_FLIFF = 'CUSTOM_FLIFF',
}

const PRICING_METHODOLOGY_MAP: Record<PricingMethodology, string> = {
  [PricingMethodology.SIMPLE_INTEREST]: 'Simple Interest',
  [PricingMethodology.DYNAMIC_RETURN_CAP]: 'Dynamic Return Cap',
  [PricingMethodology.DYNAMIC_RETURN_CAP_V2]: 'Dynamic Return Cap V2',
  [PricingMethodology.CUSTOM]: 'Custom',
  [PricingMethodology.CUSTOM_PLAYSOME]: 'Custom PlaySome',
  [PricingMethodology.CUSTOM_FLIFF]: 'Custom Fliff',
};

enum CohortStatus {
  ONGOING = 'ONGOING',
  CLOSED = 'CLOSED',
  DEFAULTED = 'DEFAULTED',
  IMPAIRED = 'IMPAIRED',
}

const COHORT_STATUS_MAP: Record<CohortStatus, string> = {
  [CohortStatus.ONGOING]: 'Ongoing',
  [CohortStatus.CLOSED]: 'Closed',
  [CohortStatus.DEFAULTED]: 'Defaulted',
  [CohortStatus.IMPAIRED]: 'Impaired',
};

enum DealStatus {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
  NONE = 'NONE',
  INACTIVE = 'INACTIVE',
}

const SEARCH_DEBOUNCE_TIME = 800;

enum PayoutStatus {
  PENDING = 'PENDING',
  INITIATED = 'INITIATED',
  PAID = 'PAID',
}

const PAYOUT_STATUS_MAP: Record<PayoutStatus, string> = {
  [PayoutStatus.PENDING]: 'Pending',
  [PayoutStatus.INITIATED]: 'Initiated',
  [PayoutStatus.PAID]: 'Paid',
};

enum CommissionType {
  PERPETUAL = 'PERPETUAL',
  COHORT_BASED = 'COHORT_BASED',
}

const COMMISSION_TYPE_MAP: Record<CommissionType, string> = {
  [CommissionType.PERPETUAL]: 'Perpetual',
  [CommissionType.COHORT_BASED]: 'Cohort Based',
};

enum TransactionType {
  COMMISSION = 'COMMISSION',
}

const TRANSACTION_TYPE_MAP: Record<TransactionType, string> = {
  [TransactionType.COMMISSION]: 'Commission',
};

const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_PAGE_SIZE = 10;

const VIRTUALIZER_OVERSCAN = 5;
const VIRTUALIZER_ESTIMATE_SIZE = 40;

enum ChartType {
  MULTI_LINE_CHART = 'MULTI_LINE_CHART',
  BAR_CHART = 'MULTI_BAR_CHART',
  LINE_BAR_CHART = 'LINE_BAR_CHART',
  TABLE = 'TABLE',
  STACKED_BAR_CHART = 'STACKED_BAR_CHART',
  BOX_PLOT_CHART = 'BOX_PLOT_CHART',
  BUBBLE_CHART = 'BUBBLE_CHART',
}

export {
  USER_ROLES,
  USER_STATUS,
  ICON_COLORS,
  STATUS_COLORS,
  ROAS_PAYBACK_PERIOD_VALUE,
  PAYBACK_PERIOD_OPTIONS,
  DEFAULT_PAYBACK_PERIOD,
  PVX_FINANCING_OPTIONS,
  DEFAULT_PVX_INTEREST,
  DEFAULT_PVX_FINANCING,
  DEFAULT_REVENUE_CHURN,
  REVENUE_CHURN_OPTIONS,
  DEFAULT_UA_INCREASE,
  UA_INCREASE_OPTIONS,
  DEFAULT_PAYBACK_PERIOD_OPTIONS,
  CHART_TAGS,
  DEFAULT_LATEST_MONTH_FINANCIALS,
  DEFAULT_MARKETING_SPENDS,
  fundingPerks,
  APP_STORE_LINK_REGEX,
  APP_STORE_LINK_REGEX_2,
  PLAY_STORE_LINK_REGEX,
  STEAM_STORE_LINK_REGEX,
  PLAY_STORE_APP_ID_REGEX,
  APP_STORE_APP_ID_REGEX,
  EMAIL_REGEX,
  CURRENCY_LIMIT_10K,
  CURRENCY_LIMIT_100K,
  CURRENCY_LIMIT_10M,
  CURRENCY_LIMIT_100M,
  MULTIPLICATION_FACTOR_DEFAULT_MONTHLY_REVENUE,
  MULTIPLICATION_FACTOR_DEFAULT_OPERATING_EXPENSES,
  MULTIPLICATION_FACTOR_DEFAULT_PREVIOUS_MONTH_BALANCE,
  APPLICATION_STORES,
  ATTRIBUTED_CHANNEL_OPTIONS,
  MONTHLY_MARKETTING_SPENDS_OPTIONS,
  TOAST_TYPES,
  SCHEDULE_CALL_LINK,
  DEMO_USER_EMAILS,
  SUBMODULE_VIEW,
  REQUESTED_SUBMODULES_META,
  SelfServeDataAccessStatus,
  MODES,
  GETTING_STARTED_MODULES,
  GETTING_STARTED_DEMO_MODULES,
  GameStatus,
  PricingMethodology,
  PRICING_METHODOLOGY_MAP,
  DealStatus,
  SEARCH_DEBOUNCE_TIME,
  PayoutStatus,
  PAYOUT_STATUS_MAP,
  CommissionType,
  COMMISSION_TYPE_MAP,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  CohortStatus,
  COHORT_STATUS_MAP,
  TransactionType,
  TRANSACTION_TYPE_MAP,
  VIRTUALIZER_OVERSCAN,
  VIRTUALIZER_ESTIMATE_SIZE,
  ChartType,
};
